<script setup lang="ts">
import {
    computed,
    defineExpose,
    defineProps,
    getCurrentInstance,
    onMounted,
    onUnmounted,
    ref,
    RenderFunction,
    withDefaults,
} from 'vue'
import LoadingSpinner from './LoadingSpinner.vue'
import { twMerge } from 'tailwind-merge'
import { InputSize } from '../../typings'

interface Props {
    loading?: boolean
    icon?: RenderFunction
    size?: InputSize
    href?: string
    transparentSpinner?: boolean
    class?: string
    sticky?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    size: 'md',
    transparentSpinner: true,
    class: '',
})

const instance = getCurrentInstance()
const elementId = ref(`BaseButton_${instance.uid}`)

defineExpose({ focusButton })

function focusButton() {
    buttonRef.value.focus()
}

const buttonSizeClasses = computed<string>(() => {
    const sizeConfig: { [key in InputSize]: string } = {
        xs: 'px-3 py-1 text-xs',
        sm: 'px-3 py-2 text-xs',
        md: 'px-4 py-2 text-sm',
        lg: 'px-5 py-2 text-base',
        xl: 'px-6 py-4 text-base md:text-lg',
    }
    return sizeConfig[props.size]
})

const iconClasses = computed(() => {
    const sizeConfig: { [key in InputSize]: string } = {
        xs: 'mr-1.5 h-3.5 w-3.5',
        sm: 'mr-1.5 h-3.5 w-3.5',
        md: 'mr-2 h-5 w-5',
        lg: 'mr-2 h-6 w-6',
        xl: 'mr-2.5 h-7 w-7',
    }
    return sizeConfig[props.size]
})

const buttonRef = ref(null)

function checkVisibility() {
    const button = document.getElementById(elementId.value)
    const clone = document.getElementById(elementId.value + '_clone')

    if (clone.getBoundingClientRect().top <= button.getBoundingClientRect().top) {
        clone.style.visibility = 'visible'
        button.style.visibility = 'hidden'
    } else {
        clone.style.visibility = 'hidden'
        button.style.visibility = 'visible'
    }
}

function makeClone(): void {
    removeClone()
    const button = document.getElementById(elementId.value)
    let clone = button.cloneNode(true)
    clone.id += '_clone'
    clone.style.left = button.getBoundingClientRect().left + 'px'
    clone.style.width = button.offsetWidth + 'px'
    clone.onclick = () => button.click()
    document.querySelector('.frontend-v2').appendChild(clone)
    checkVisibility()
}

function removeClone(): void {
    document.getElementById(elementId.value + '_clone')?.remove()
}

onMounted(() => {
    if (props.sticky) {
        makeClone()
        window.addEventListener('scroll', checkVisibility)
        window.addEventListener('resize', makeClone)
    }
})

onUnmounted(() => {
    if (props.sticky) {
        removeClone()
        window.removeEventListener('scroll', checkVisibility)
        window.removeEventListener('resize', makeClone)
    }
})
</script>

<template>
    <Component
        :is="props.href ? 'a' : 'button'"
        :id="elementId"
        ref="buttonRef"
        :href="props.href"
        v-bind="$attrs"
        :disabled="props.loading"
        :class="
            twMerge(
                'inline-flex items-center justify-center rounded-md font-medium no-underline shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2',
                buttonSizeClasses,
                props.class,
                props.sticky ? 'sticky bottom-10' : ''
            )
        ">
        <LoadingSpinner v-if="props.loading" :transparent="props.transparentSpinner" :class="iconClasses" />
        <Component :is="props.icon" v-else-if="!!props.icon" :class="iconClasses" />
        <slot></slot>
    </Component>
</template>

<style scoped></style>
